import { useForm } from "@mantine/form"
import { useEffect, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Turnstile } from "@marsidev/react-turnstile"
import { useTurnstile } from "../../components/useTurnstile"
import { trackEvent } from "../../lib/analytics"
import { captureCaughtError } from "../../lib/errorReporting"
import { signupValues } from "../../lib/autofillSync"
import { suggestEmailDomain } from "../../lib/emailDomainSuggestion"
import { renderCopy } from "../../components/renderCopy"
import { caseFileCountVars } from "../../lib/caseFileCopy"
import { funnelAdvances, restoredAnswerPrefix } from "../../lib/funnelQualification"

// THE SIGN-UP FORM ASKS FOR THREE FIELDS (act:5ce6caf8). Phone and the five
// address fields MOVED to the signing page as an optional disclosure — this is
// the screen a paid ad click has to survive, and the firm needs none of that
// information until it is about to file. US_STATES moved with them, to
// ../../lib/usStates, because two components now render the same dropdown.

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
const primaryBtn =
  "w-full py-3 bg-brand text-white rounded-md text-base font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
// Both ANSWER buttons in the question step render with this ONE constant
// (act:75fefd8d, operator decision 2026-08-16, extending act:022ed6f6's
// case-file call): a question's qualifying answer can be either polarity
// (`expected: false` is live — the resort-fees "was the full price shown"
// question), so emphasising either side is arbitrary at best and leading at
// worst, and these answers are attested into qualification_answers. One
// constant used twice, so the pair cannot drift apart. The disqualified
// email-capture button and the Sign Up submit keep primaryBtn — those are
// commits, not answers.
//
// Since 2026-08-20 the pair is a navy OUTLINE rather than a grey fill
// (operator decision, the same one that put this section on a brand-navy
// ground). The defect it fixes is WEIGHT, not polarity: grey-on-white made the
// first click of the whole funnel the palest element on the page, while the
// two buttons that merely SCROLL the reader here — the pinned nav CTA and the
// hero CTA — were navy-FILLED. That is inverted hierarchy at the point of
// commitment. Raising both sides by exactly the same amount raises the
// QUESTION, never an answer, so the no-steer property above is untouched and
// still the reason there is one constant rather than two.
// text-brand on white measures 10.4:1 (the retired text-gray-800 on gray-100
// cleared 4.5:1); keep the hover identical on both or the steer quietly
// returns. This pair is legible ONLY because it renders inside the white
// wizardCard; read the BRAND-NAVY GROUND note in Landing.jsx before moving
// either button out of that card, where navy-on-navy would be invisible. That
// containment is also why the focus ring's offset is the default WHITE: the
// 2px gap lands on the card, ~24px inside its edge, never on the navy section.
//
// TWIN: YourFile.jsx's neutralBtn (the case-file interview's answer pair).
// Since act:4a2eecd5 the two share ONE colour + focus core, token for token,
// and spec/frontend/answer_button_parity_spec.rb fails if they drift. Only the
// LAYOUT differs (w-full/rounded-md/text-base here, flex-1/px-6/rounded-lg
// there) plus disabled:opacity-60, which only the case-file pair needs because
// only it disables while a POST is in flight. Change the core in both files or
// in neither. The earlier note here — that the funnel deliberately had no
// focus ring and kept the browser default — was retired by act:4a2eecd5.
//
// focus:outline-HIDDEN, never outline-none: in Tailwind v4 `outline-none` is
// `outline-style:none` with no fallback, while `outline-hidden` adds
// `@media (forced-colors: active) { outline: 2px solid transparent }`. The ring
// is a box-shadow and box-shadow is NOT painted in forced-colors mode, so
// pairing a ring with outline-none leaves Windows High Contrast users with no
// focus indicator at all (measured; act:8c0f8059 sweeps the other 22 sites).
// hover/active are `enabled:`-guarded so the pair cannot split weight while
// disabled, and `active:` is what gives a TOUCH tap any feedback at all —
// Tailwind v4 gates `hover:` behind @media (hover: hover), so a phone never
// sees it (async-feedback.md).
const neutralBtn =
  "w-full py-3 rounded-md text-base " +
  "bg-white text-brand border-2 border-brand font-semibold transition-colors " +
  "enabled:hover:bg-brand enabled:hover:text-white " +
  "enabled:active:bg-brand enabled:active:text-white " +
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
// Wizard card chrome (Establishment direction, act:bf2ebd5f) — shared by the
// question / disqualified / contact steps so the wizard reads as one framed
// card across its states. Border + shadow only: no transform, so the
// Turnstile iframe inside the contact step is unaffected.
//
// shadow-xl, not shadow-sm, since 2026-08-20: this card now floats on the
// signup section's brand-navy ground rather than on white, and a 1px
// gray-200 border does no separating work against navy — the elevation is
// what makes it read as a card sitting ON the section. The border is kept
// so the card still frames correctly if it is ever rendered on a light
// ground again.
const wizardCard = "bg-white rounded-xl border border-gray-200 shadow-xl"
// Keyboard-only focus ring for programmatically-focused step headings.
// Matches the funnel convention in FAQ.jsx — brand-colored, no ring on mouse click.
const headingFocusClass = "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"

function funnelPath(target, action) {
  const base = target.theory_slug ? `/${target.slug}/${target.theory_slug}` : `/${target.slug}`
  return action ? `${base}/${action}` : base
}

// ── Wizard state persistence (act:ecf1cd0c) ─────────────────────────────────
// Any remount (error re-render of a different component, dev full-reload, a
// plain page reload) used to silently reset the wizard to question 1 because
// ALL state was useState — a visitor mid-form lost everything. We persist
// ONLY { step, answers } (quiz progress) in sessionStorage.
//
// NEVER persist contact-form values, capturedEmail, fee_type_described,
// marketing_consent, or the Turnstile token: name and email are PII and browsers
// restore sessionStorage when a closed tab is reopened — on a shared computer an
// abandoned funnel must not resurrect them (and the Turnstile token, which lives
// in useTurnstile's state, is single-use; replaying it guarantees a server
// reject). A remount restores the visitor to the contact step at worst —
// acceptable loss.
// Contact-field persistence is a separate operator decision; do not add it.

// djb2 over the ordered question-id list. A question-set change (add /
// remove / reorder) changes the storage key, invalidating stale entries
// wholesale — defense-in-depth on top of the per-answer restore validation.
//
// ⚠ ID-ONLY BY CHOICE, diverged from its sibling (act:44fb2908): the
// case-file interview's storageKey (lib/caseFileInterview.js) also
// fingerprints each question's MEANING — expected bucket, list-wording
// presence — so an id-preserving authoring edit orphans its snapshots. Here
// the per-answer restore validation re-gates each stored answer, so a
// gating `expected` flip already drops the stale answer on restore. The one
// gap that leaves: a gating→informational flip (true → null) keeps a banked
// answer under the changed definition — tracked as act:e5384de4; close it
// there rather than quietly widening this hash.
function hashQuestionIds(questions) {
  const s = questions.map((q) => q.id).join("\n")
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0
  return h.toString(36)
}

// Keyed by campaign + theory + question-set so progress can never leak
// across campaigns (a returning visitor entering a second target starts
// that wizard fresh — scenario 16).
function wizardStorageKey(target, questions) {
  return `qualform:${target.slug}:${target.theory_slug || ""}:${hashQuestionIds(questions)}`
}

// Fail-closed restore: restored answers feed qualification_answers — the
// attested legal record — so nothing is trusted from storage. Only answers
// that CLEAR the CURRENT question are kept (a gating question needs a boolean
// strictly equal to its `expected`; an INFORMATIONAL one accepts either
// answer — act:6d293884; and a stale id matches no current question), and only
// as a contiguous prefix in question order. The stored `step` is never
// trusted: it's recomputed as the length of that prefix, so a newly-inserted
// question can't be skipped. Unintelligible stored state restores NOTHING.
//
// The rule itself lives in lib/funnelQualification.js so it can be executed
// under node and locked by the parity harness — this wrapper owns only the
// storage read and the never-throw contract.
function restoreWizardState(key, questions) {
  const empty = { step: 0, answers: {} }
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return empty
    const stored = JSON.parse(raw)?.answers
    if (!stored || typeof stored !== "object") return empty
    return restoredAnswerPrefix(questions, stored)
  } catch {
    return empty
  }
}

export default function QualForm({
  target,
  legal_theory,
  case_file_count,
  turnstile_site_key,
  error,
  preview,
  marketing_consent_copy,
  onDisqualifiedChange,
}) {
  const questions = legal_theory.qualifying_questions || []
  // Mounted-under component name — terminal detection in the submit handler
  // compares against this instead of repeating the "Funnel/Landing" literal
  // (the server side has one constant for it: FunnelController::LANDING_COMPONENT).
  const { component: mountedComponent, props: pageProps } = usePage()
  // The bot-check widget's one wiring (act:1ca3011b) — see the hook's header
  // for what each piece answers. The token is NOT a form value: it is held here
  // and posted alongside the values, so a re-render of the contact fields can
  // never touch it and the sessionStorage persist can never see it.
  const turnstile = useTurnstile({
    siteKey: turnstile_site_key,
    // A widget that fails on the CLIENT never reaches the server, so the
    // server's rejection telemetry cannot count it. This reports it through
    // the browser error pipe (scrubbed, allowlisted, never throws). Sentry
    // groups by stack trace before message, and every one of these is thrown
    // from this one line — so expect ONE issue, with the outcome and
    // Cloudflare's client code in each event's message. Not an analytics
    // event on purpose — it is a fault, not a funnel step, and the GA4 bridge
    // holds its funnel vocabulary to the dashboard's step list.
    onFailure: ({ outcome, code }) => {
      const fault = new Error(`Turnstile widget ${outcome}${code ? ` (${code})` : ""}`)
      fault.name = "TurnstileWidgetFailure"
      captureCaughtError(fault)
    },
  })
  const storageKey = wizardStorageKey(target, questions)
  // Quiz progress ALWAYS starts empty on the first render. The landing is
  // server-rendered (act:7079ffd1), and hydration requires the first client
  // render to match the server markup — which was produced with no
  // sessionStorage. The restore happens in a post-mount effect below; a
  // returning mid-quiz visitor lands on their step one commit later. What is and
  // is not mechanically guarded is stated once, in Reveal.jsx (act:35de96ff):
  // seeding this from sessionStorage at render time would NOT red CI, because
  // the read is wrapped and the divergence throws nothing.
  const [step, setStep] = useState(0)
  // Gates the persist effect until the restore has run — without it, the
  // mount pass would persist { step: 0 } over the stored progress before the
  // restore effect ever read it. State (not a ref) on purpose: the persist
  // effect must re-run when this flips.
  const [restoredOnce, setRestoredOnce] = useState(false)
  const [disqualified, setDisqualified] = useState(null)
  const [capturedEmail, setCapturedEmail] = useState("")
  // The re-marketing capture's own error line (act:7112377e) — see the input.
  const [captureError, setCaptureError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  // Accumulated quiz answers keyed by question id. Re-answering (via Back)
  // overwrites the prior value; "Start over" clears the whole map.
  const [answers, setAnswers] = useState({})

  // Persist quiz progress; clear on the disqualification terminal state
  // (clearing on submit success happens in the submit handler, where the
  // server's response component is known). Best-effort: storage being
  // unavailable (private mode, quota) must never break the wizard.
  useEffect(() => {
    if (preview || !restoredOnce) return
    try {
      if (disqualified) {
        window.sessionStorage.removeItem(storageKey)
      } else {
        window.sessionStorage.setItem(storageKey, JSON.stringify({ step, answers }))
      }
    } catch {
      // sessionStorage unavailable — persistence is an enhancement only.
    }
  }, [step, answers, disqualified, preview, storageKey, restoredOnce])

  // Move keyboard/SR focus to the new step's heading on each transition.
  // Skip the first render so page load doesn't yank focus/scroll to the form.
  const focusRef = useRef(null)
  const firstRender = useRef(true)
  const formStartTracked = useRef(false)
  // The contact-step <form>, so the submit handler can read what is actually in
  // the three boxes (act:7112377e — see lib/autofillSync.js).
  const contactFormRef = useRef(null)

  // "Did you mean …@gmail.com?" on the email field (act:27d97c7e).
  //
  // Held in state as the SUGGESTION only — never as the field's value. The
  // three sign-up inputs are deliberately UNCONTROLLED (act:7112377e), so
  // React does not own what is in the box and must not start pretending to.
  const [emailSuggestion, setEmailSuggestion] = useState(null)

  // The DOM node is the authority for this field, so the check reads it rather
  // than form state. That is not a stylistic choice: an autofilled address
  // never reaches `form.values` until the submit-time merge, so a check driven
  // off state would never fire for anyone using a password manager — which is a
  // large share of a mobile ad audience.
  const emailInputEl = () => contactFormRef.current?.elements?.email || null

  // Fires on BLUR, not on every keystroke. `…@gmail.co` is one edit from
  // `gmail.com`, so a per-keystroke check would nag every visitor halfway
  // through typing a correct address and teach them to ignore it.
  const checkEmailSuggestion = () => setEmailSuggestion(suggestEmailDomain(emailInputEl()?.value))

  // Applying the correction WRITES THE DOM NODE, and this is the whole reason
  // the handler exists rather than a form.setFieldValue call. The submit
  // wrapper runs `signupValues(contactFormRef.current, …)`, whose
  // mergeAutofilled lets a NON-BLANK DOM value win over held state — so a
  // correction applied only to React state would be silently reverted at
  // submit and the visitor would post the typo anyway, having watched the box
  // not change. Write the node, then sync state to match it.
  const applyEmailSuggestion = () => {
    const el = emailInputEl()
    if (!el || !emailSuggestion) return

    el.value = emailSuggestion.full
    form.setFieldValue("email", emailSuggestion.full)
    setEmailSuggestion(null)
    el.focus()
  }
  const dqEmailRef = useRef(null)

  // The post-mount restore (hydration-safe half of the sessionStorage
  // persistence — see the step/restoredOnce declarations above). Admin
  // previews never persist or restore: a draft campaign's quiz state has no
  // business surviving in the browser. When it does restore, the step change
  // it causes is a page-load artifact, not a user transition — the focus
  // effect below must not yank scroll/focus to the form for it.
  const skipNextFocus = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on storageKey. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on questions. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on preview. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    if (!preview) {
      const restored = restoreWizardState(storageKey, questions)
      if (restored.step > 0) {
        skipNextFocus.current = true
        setStep(restored.step)
        setAnswers(restored.answers)
      }
    }
    setRestoredOnce(true)
    // Mount-only: the restore is a one-shot page-load concern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook specifies more dependencies than necessary: step, disqualified. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (skipNextFocus.current) {
      skipNextFocus.current = false
      return
    }
    focusRef.current?.focus()
  }, [step, disqualified])

  // Tell the parent (Landing) whether we're in the disqualified terminal
  // state, so it can suppress the "Sign Up With…" section heading — otherwise
  // the sign-up invitation sits directly above the "We're sorry" message.
  useEffect(() => {
    onDisqualifiedChange?.(!!disqualified)
  }, [disqualified, onDisqualifiedChange])

  const contactFormReached = step >= questions.length && !disqualified && questions.length > 0
  const contactFormTracked = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on target.theory_slug. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on target.slug. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on preview. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    if (contactFormReached && !contactFormTracked.current && !preview) {
      contactFormTracked.current = true
      trackEvent("contact_form_reached", {
        target_slug: target.slug,
        theory_slug: target.theory_slug,
      })
    }
  }, [contactFormReached])

  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      fee_type_described: "",
      // Opt-out posture: pre-checked when the server delivered the copy.
      // null = the box was never shown — the server records nothing and
      // suppresses nothing for a question that wasn't asked.
      marketing_consent: marketing_consent_copy ? true : null,
      qualification_answers: [],
    },
    validate: {
      first_name: (v) => (v ? null : "Required"),
      last_name: (v) => (v ? null : "Required"),
      email: (v) => (/\S+@\S+\.\S+/.test(v) ? null : "Valid email required"),
    },
  })

  // THE THREE SIGN-UP FIELDS ARE UNCONTROLLED (act:7112377e). Mantine hands us
  // a `value` for each; we drop it and pass the same string as `defaultValue`
  // instead, keeping its onChange so form.values still tracks ordinary typing.
  //
  // The reason is the WIPE: React re-applies node.value from the value prop on
  // every commit of a controlled input, not only when the prop changed. On THIS
  // step the Turnstile token lands a second or two after render and re-renders
  // the whole form — so a password manager that filled the name and email
  // without React noticing had its work erased in front of the visitor. That is
  // a very good candidate for the "slightly weird behavior" this action was
  // filed on. Uncontrolled, React never touches the node, and the submit
  // handler below reads whatever is actually in the boxes.
  //
  // `name` is what a password manager weights most heavily for field detection,
  // and it is how readAutofilled addresses each box. Nothing native-submits
  // this form (Mantine's onSubmit preventDefaults, and the contact step is
  // only reachable post-mount even under SSR — server HTML carries at most
  // question 1), and the form tag carries method="post" as belt-and-braces —
  // see the comment there — so the names cannot reach a URL.
  const autofillProps = (field) => {
    const { value, ...rest } = form.getInputProps(field)
    return { ...rest, name: field, defaultValue: value }
  }

  const submitQualification = form.onSubmit(async (values) => {
    setSubmitting(true)
    trackEvent("qualify_submit", { target_slug: target.slug, theory_slug: target.theory_slug })
    // Serialize the attested answers in question order. By this step every
    // question has been answered; guard `!== undefined` so a legit "No"
    // (false) answer isn't dropped.
    const qualification_answers = questions
      .filter((q) => answers[q.id] !== undefined)
      .map((q) => ({ id: q.id, text: q.text, answer: answers[q.id] }))
    // A tap that beats the widget waits a bounded few seconds for the token,
    // then posts EITHER WAY — the server is the only gate, and a refusal there
    // is counted with Cloudflare's reason. Never a disabled button: see
    // useTurnstile's header for why (act:1ca3011b).
    const turnstile_token = await turnstile.awaitToken()
    // preserveState keeps this component mounted so a server-side error
    // (re-rendered Landing with an `error` prop) doesn't wipe the visitor's
    // typed contact info and answered questions back to Q1.
    router.post(
      funnelPath(target, "qualify"),
      { ...values, qualification_answers, turnstile_token },
      {
        preserveState: true,
        onSuccess: (page) => {
          // Terminal: the server moved us off this page (Signing,
          // AlreadySignedUp, CampaignClosed) — drop the persisted quiz
          // progress. An error re-render of the SAME component keeps it,
          // so a reload after a transient failure still restores.
          //
          // Inertia lands the server's 422 re-render HERE, not in onError:
          // it routes on `props.errors` (plural), and this page's refusal
          // carries `error` (singular). Verified against @inertiajs/core 3.3.
          if (page?.component !== mountedComponent) {
            try {
              window.sessionStorage.removeItem(storageKey)
            } catch {
              /* best-effort */
            }
          } else {
            // The server refused and re-rendered us. The token we posted is
            // spent whatever the reason — a Turnstile token verifies ONCE —
            // so the retry needs a fresh challenge, or "Please try again" is
            // a promise the next tap cannot keep (timeout-or-duplicate).
            // `spend` keeps a token that landed AFTER the post, if any.
            turnstile.spend(turnstile_token)
          }
        },
        onFinish: () => setSubmitting(false),
      },
    )
  })

  const lc = target.landing_content || {}
  const privacyDisclosure =
    lc.privacy_disclosure ||
    "Your privacy and data protection are important to us. Information you provide will be used solely for the purpose of evaluating and pursuing your legal claim."
  const privacyPolicyUrl = lc.privacy_policy_url || "/privacy"
  // Fee disclosure: the campaign's Ed-editable landing copy when present
  // (the seeded default names the fee via the server-resolved {{fee_percent}}
  // token), else a fee-aware fallback built from the SINGLE fee source
  // (act:86af7e8e — legal_theory.fee_percent is the EFFECTIVE override-aware
  // value FunnelProps#theory_props exposes; no hardcoded percentage). When the
  // fee is somehow absent, the fallback degrades to the fee-agnostic wording.
  const feePct = legal_theory.fee_percent
  const feeDisclosure =
    lc.fee_disclosure ||
    (feePct
      ? `You don't pay anything unless we recover compensation on your behalf. Our legal team works on contingency — we collect ${feePct}% of the recovery only if you win, and nothing if you don't.`
      : "You don't pay anything unless we recover compensation on your behalf. Our legal team works on contingency, which means we only get paid if you do.")

  // Overall progress across qualifying questions + the final contact step.
  const totalSteps = questions.length + 1
  const currentStep = Math.min(step + 1, totalSteps)
  const pct = Math.round((currentStep / totalSteps) * 100)
  // Visual progress bar only — aria-hidden. The screen-reader progress
  // announcement is a SEPARATE stable aria-live region (stepAnnouncement, below)
  // rendered OUTSIDE the per-step keyed wrapper, so it updates in place and
  // actually announces each step. A live region INSIDE the `key={`q-${step}`}`
  // wrapper remounts every step and a freshly-mounted live region is silent —
  // which is why step changes never announced before (acc-0025).
  const progressBar = (
    <div className="mb-6" aria-hidden="true">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>
          Step {currentStep} of {totalSteps}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-brand transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )

  // Stable SR-only progress announcement. Rendered as a sibling OUTSIDE the
  // per-step keyed wrapper (see the qualifying-question return) so React keeps
  // the SAME node across steps and aria-live announces each change in place. It
  // announces ONLY the step position; the question text is announced by the
  // focus-move to the step heading, so the heading carries no aria-live (that
  // would double-speak the question, acc-0025).
  const stepAnnouncement = (
    <p className="sr-only" aria-live="polite">
      Step {currentStep} of {totalSteps}
    </p>
  )

  // Defense-in-depth: the model guarantees >= 1 question, but never admit
  // everyone if a malformed prop slips through.
  if (questions.length === 0) {
    return (
      <div id="qualify" className="max-w-xl mx-auto p-6 text-center text-gray-600">
        This claim isn't accepting submissions right now. Please check back soon.
      </div>
    )
  }

  // Qualifying questions, one per step.
  if (step < questions.length && !disqualified) {
    const q = questions[step]
    const answer = (given) => {
      if (!formStartTracked.current && !preview) {
        formStartTracked.current = true
        trackEvent("form_start", { target_slug: target.slug, theory_slug: target.theory_slug })
      }
      // `step` is zero-based, so the first question sends `step: 0`. The GA4 leg
      // filters on the KEY and tests the value's TYPE, never its truthiness —
      // a truthiness test here would silently drop the most common step of all.
      if (!preview)
        trackEvent("question_answered", {
          target_slug: target.slug,
          theory_slug: target.theory_slug,
          question_id: q.id,
          step: step,
        })
      // Record the attested answer (overwrites on Back-then-re-answer).
      setAnswers((prev) => ({ ...prev, [q.id]: given }))
      // INTENTIONAL DIVERGENCE (change-closure rule): the paid funnel keeps its
      // OWN rule, deliberately separate from the case-file lane's
      // (act:22274f4d §6 is new-endpoint-only). It moved out of this file into
      // lib/funnelQualification.js at act:6d293884 — still a separate
      // implementation, nothing here imports lib/qualificationGate.js — purely
      // so the harness can execute it over declared verdicts. This was the one
      // comparison site with no executable lock, on the surface that carries
      // paid traffic. If funnel enforcement is ever un-gated (act:06c0f539),
      // converge funnelQualification.js onto qualificationGate.js rather than
      // keeping a third copy.
      //
      // An INFORMATIONAL question (no expected answer) advances on EITHER
      // answer and never reaches the disqualified branch below.
      if (funnelAdvances(q, given)) setStep(step + 1)
      else setDisqualified({ message: q.disqualification_message, question_id: q.id })
    }
    return (
      <>
        {/* stepAnnouncement is OUTSIDE the keyed wrapper so it survives the
            per-step remount and announces progress in place (acc-0025). */}
        {stepAnnouncement}
        <div
          id="qualify"
          key={`q-${step}`}
          className={`max-w-xl mx-4 sm:mx-auto p-6 step-enter ${wizardCard}`}
        >
          {progressBar}
          {/* Focus moves here on each step (focusRef), which announces the
              question text to screen readers — so this carries no aria-live. */}
          <div ref={focusRef} tabIndex={-1} className={`mb-6 rounded-md ${headingFocusClass}`}>
            <h2 className="font-serif text-xl font-bold text-ink">{q.text}</h2>
          </div>
          <div className="flex gap-4">
            <button type="button" onClick={() => answer(true)} className={neutralBtn}>
              Yes
            </button>
            <button type="button" onClick={() => answer(false)} className={neutralBtn}>
              No
            </button>
          </div>
          {step > 0 && (
            <button
              type="button"
              className="mt-2 inline-flex items-center min-h-[44px] px-2 -mx-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              onClick={() => setStep(step - 1)}
            >
              &larr; Back
            </button>
          )}
        </div>
      </>
    )
  }

  // Disqualification branch — capture email for re-marketing.
  // Copy + layout mirror the full-page Disqualified.jsx so the same outcome
  // (inline panel vs. full page) reads the same to the visitor. The heading +
  // NQL-capture prompt are Ed-tweakable exit copy (act:8a94f3d7), read from the
  // campaign landing content with TODAY's wording as the fallback; the
  // disqualified_heading key is the SAME one Disqualified.jsx reads, so the
  // inline panel and the full page stay in sync.
  if (disqualified) {
    const disqualifiedHeading = lc.disqualified_heading || "We're sorry"
    const capturePrompt =
      lc.nql_capture_prompt || "Leave your email and we'll notify you if this changes."
    // The door copy + chrome values ride the page props (landing_props ships
    // case_file_copy; firm_name/site_name/contact_email are global shared
    // props), read here via usePage so Landing doesn't thread four more
    // pass-through props. One map per FirmDetail::CASE_FILE_DOOR_TOKENS —
    // see the parity spec.
    const {
      firm_name,
      site_name,
      contact_email,
      case_file_company_count,
      case_file_copy = {},
    } = pageProps
    const caseFileVars = {
      firm_name,
      site_name,
      contact_email,
      company_name: target.company_name,
      ...caseFileCountVars(case_file_count, case_file_company_count),
    }
    return (
      <div
        id="qualify"
        key="disqualified"
        className={`max-w-xl mx-4 sm:mx-auto p-6 step-enter text-center ${wizardCard}`}
      >
        {/* Focus moves here on disqualification, which announces the heading +
            message to screen readers — so no aria-live (it would double-speak,
            and a remounted live region is silent anyway, acc-0025). */}
        <div ref={focusRef} tabIndex={-1} className={`rounded-md ${headingFocusClass}`}>
          <h2 className="font-serif text-2xl font-bold text-ink mb-4">{disqualifiedHeading}</h2>
          {/* The per-question reason is OPTIONAL and is blank on every production
              question today (act:e18c3635) — see the twin guard in
              Disqualified.jsx for the full reasoning. Unguarded, this rendered an
              EMPTY paragraph on the live paid funnel; guarded, the panel degrades
              to heading + capture prompt, which stand on their own. Change both
              sites together (.claude/rules/change-closure.md). */}
          {disqualified.message && (
            <p className="text-lg text-gray-600 mb-3">{disqualified.message}</p>
          )}
        </div>
        {/* Door 2 of the case-file surface (act:22274f4d): the miss offers
            the interview FIRST — the visitor is still anonymous, the
            interview asks nothing personal, and the email capture below
            demotes to last resort. Twin: Disqualified.jsx renders the same
            door post-capture, from the same case_file_door_* keys — one
            editable wording for both (change-closure). The panel's container
            is text-center; no per-block override, so the button no longer
            hugs the left edge on desktop. */}
        {case_file_count > 0 && (
          <div
            className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4"
            data-testid="case-file-door"
          >
            <p className="font-semibold text-ink text-sm mb-1">
              {renderCopy(case_file_copy.case_file_door_heading, caseFileVars)}
            </p>
            <p className="text-sm text-gray-600 mb-3">
              {renderCopy(case_file_copy.case_file_door_body, caseFileVars)}
            </p>
            <a
              href={`/file?miss_target=${target.slug}&miss_theory=${target.theory_slug || ""}`}
              className="block w-full sm:w-auto sm:inline-block text-center bg-brand text-white text-sm font-semibold rounded-md px-4 py-2.5 hover:bg-brand-hover"
            >
              {renderCopy(case_file_copy.case_file_door_button, caseFileVars)}
            </a>
          </div>
        )}
        <p
          className={
            case_file_count > 0 ? "text-xs text-gray-500 mb-2" : "text-sm text-gray-600 mb-3"
          }
        >
          {case_file_count > 0
            ? `No thanks — ${capturePrompt.charAt(0).toLowerCase()}${capturePrompt.slice(1)}`
            : capturePrompt}
        </p>
        <label htmlFor="dq-email" className="sr-only">
          Email address
        </label>
        {/* Uncontrolled, and the button no longer gates on React having seen the
            value (act:7112377e). This box lives outside any <form>, so it kept
            the worst version of the controlled-input problem: a password
            manager filling it silently left a visibly-populated field beside a
            permanently DISABLED button, with no way forward at all. The ref is
            the DOM's own value and is the authority at click.

            `capturedEmail` no longer decides anything — it is kept only to seed
            defaultValue if this branch remounts. Do not restore a gate on it;
            that gate WAS the dead end.

            Trading the disabled button for an enabled one means an empty click
            is now a detected error, and a detected error has to SAY something
            (WCAG 3.3.1, and .claude/rules/self-documenting-ui: say what went
            wrong AND what to do). Moving focus alone is a cue for a sighted
            mouse user and nothing at all for anyone else, so this carries the
            same aria-invalid + aria-describedby + role="alert" shape the three
            sign-up fields below use. */}
        <input
          id="dq-email"
          ref={dqEmailRef}
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email"
          defaultValue={capturedEmail}
          onChange={(e) => {
            setCapturedEmail(e.target.value)
            if (captureError) setCaptureError(null)
          }}
          aria-invalid={!!captureError}
          aria-describedby={captureError ? "dq-email-error" : undefined}
          className={`${inputClass} mb-3`}
        />
        {captureError && (
          <p id="dq-email-error" role="alert" className="text-xs text-rose-600 mb-3 text-left">
            {captureError}
          </p>
        )}
        <button
          type="button"
          className={primaryBtn}
          disabled={submitting}
          onClick={() => {
            const email = (dqEmailRef.current?.value || "").trim()
            if (!email) {
              setCaptureError("Enter your email address so we can notify you.")
              dqEmailRef.current?.focus()
              return
            }
            setCaptureError(null)
            setSubmitting(true)
            router.post(
              funnelPath(target, "non_qualifying"),
              {
                email,
                question_id: disqualified.question_id,
                disqualification_message: disqualified.message,
              },
              {
                onFinish: () => setSubmitting(false),
              },
            )
          }}
        >
          Submit
        </button>
        <p className="text-xs text-gray-500 mt-2">Enter your email above to be notified.</p>
        <button
          type="button"
          className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          onClick={() => {
            setDisqualified(null)
            setStep(0)
            setAnswers({})
          }}
        >
          Start over
        </button>
      </div>
    )
  }

  // The submit's label while it waits: a tap that beats the widget waits up to
  // TOKEN_GRACE_MS before posting, and a disabled button that still says
  // "Sign Up" for five seconds is the silence that invites a reload. The
  // button stays ENABLED until tapped — never gated on the token (see
  // useTurnstile's header for the decision).
  const submitLabel = preview ? "Disabled in preview" : submitting ? "One moment…" : "Sign Up"

  // Final step: contact info + cross-sell + Turnstile.
  // Opacity-only entrance (no transform / no remount) so the Turnstile iframe
  // is never inside a transformed or re-keyed ancestor — protects the token.
  return (
    <div id="qualify" className={`max-w-xl mx-4 sm:mx-auto p-6 animate-fade-in ${wizardCard}`}>
      {/* No stepAnnouncement here — this terminal step announces via the
          focus-move to the "You may qualify" heading below, not a progress live
          region (acc-0025). progressBar stays for the visual-only bar at 100%. */}
      {progressBar}
      <h3
        ref={focusRef}
        tabIndex={-1}
        className={`font-serif text-2xl font-bold text-ink mb-1 rounded-md ${headingFocusClass}`}
      >
        You may qualify
      </h3>
      <p className="text-gray-600 mb-4">Tell us how to reach you and we'll take it from here.</p>

      <p className="mb-6 text-xs text-gray-600 leading-relaxed">
        {privacyDisclosure}{" "}
        <a
          href={privacyPolicyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-action underline"
        >
          Privacy Policy
        </a>
      </p>

      {error && (
        <div role="alert" className="mb-4 p-3 bg-rose-50 text-rose-700 rounded-md text-sm">
          {error}
        </div>
      )}

      <form
        ref={contactFormRef}
        // method="post" is BELT-AND-BRACES against a native submit ever
        // firing without preventDefault (act:7079ffd1). Today no such window
        // exists: the landing is server-rendered, but server HTML only ever
        // carries question 1 or the zero-questions static fallback — this
        // contact step is reachable strictly post-mount (interaction or the
        // sessionStorage restore effect), so the form never exists before
        // React attaches (CP3 automation verified the branch). The guard is
        // kept because the cost is zero and the failure it caps is severe: a
        // form with no method native-submits as GET, putting the visitor's
        // name and email into the query string, browser history and the
        // Referer header. With POST, the worst any future regression can do
        // is a 404 on an unrouted POST with the fields in the body.
        method="post"
        onSubmit={
          preview
            ? (e) => e.preventDefault()
            : (e) => {
                // preventDefault FIRST, before any application code. Mantine's own
                // onSubmit wrapper calls it, but that now runs a line below us — and
                // the three inputs carry `name`, so a throw in between would produce a
                // native submit putting the visitor's contact fields on the wire (the
                // method="post" above keeps them out of the URL even then). Nothing in
                // the next line throws today; this is one line so that stays true as
                // handlers accumulate. Signing.jsx does the same, for the same reason.
                e.preventDefault()
                // READ THE BOXES BEFORE VALIDATING THEM (act:7112377e). The three
                // inputs are uncontrolled, so a value only reaches Mantine when its
                // onChange fired — and a password manager that writes the DOM node
                // directly never fires one. Without this, validation would reject
                // "Required" on a field the visitor can plainly see filled in, and
                // the firm would get nothing.
                //
                // Ordering is load-bearing and was verified against @mantine/form
                // 9.2's source rather than assumed: setValues writes refValues.current
                // SYNCHRONOUSLY, and both validate() and the submit handler read that
                // ref — so a same-tick setValues here is seen by the validation that
                // runs a line later. It must sit OUTSIDE form.onSubmit(), which
                // validates before it calls anything of ours.
                form.setValues(signupValues(contactFormRef.current, form.getValues()))
                submitQualification(e)
              }
        }
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
              First name
            </label>
            <input
              id="first_name"
              {...autofillProps("first_name")}
              autoComplete="given-name"
              placeholder="First name"
              className={inputClass}
              aria-invalid={!!form.errors.first_name}
              aria-describedby={form.errors.first_name ? "first_name-error" : undefined}
            />
            {form.errors.first_name && (
              <p id="first_name-error" role="alert" className="text-xs text-rose-600 mt-1">
                {form.errors.first_name}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
              Last name
            </label>
            <input
              id="last_name"
              {...autofillProps("last_name")}
              autoComplete="family-name"
              placeholder="Last name"
              className={inputClass}
              aria-invalid={!!form.errors.last_name}
              aria-describedby={form.errors.last_name ? "last_name-error" : undefined}
            />
            {form.errors.last_name && (
              <p id="last_name-error" role="alert" className="text-xs text-rose-600 mt-1">
                {form.errors.last_name}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="email"
            {...autofillProps("email")}
            type="email"
            autoComplete="email"
            placeholder="Email"
            className={inputClass}
            onBlur={checkEmailSuggestion}
            aria-invalid={!!form.errors.email}
            aria-describedby={
              [
                form.errors.email ? "email-error" : null,
                emailSuggestion ? "email-suggestion" : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
          />
          {form.errors.email && (
            <p id="email-error" role="alert" className="text-xs text-rose-600 mt-1">
              {form.errors.email}
            </p>
          )}
          {/* SUGGESTS, NEVER REFUSES (act:27d97c7e). Nothing here touches
              validation or the submit button — a visitor who ignores this can
              submit exactly what they typed. It is deliberately not
              role="alert": this is an offer, not an error, and announcing it as
              one would tell a screen-reader user their correct address is
              wrong. The whole thing is a button so it is one tap on a phone,
              which is where the paid traffic lands. */}
          {emailSuggestion && (
            <p id="email-suggestion" className="text-xs text-gray-700 mt-1">
              Did you mean{" "}
              <button
                type="button"
                onClick={applyEmailSuggestion}
                className="font-medium text-brand underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
              >
                {emailSuggestion.full}
              </button>
              ? If not, keep what you typed.
            </p>
          )}
        </div>

        {/* The cross-sell checkboxes that stood here are DELETED
            (act:22274f4d): a bare company name with no issue statement and no
            eligibility gate was the blind-shopping defect. Their replacement
            is the case-file interview, offered AFTER signing (the
            confirmation invitation) — where every extra claim states its
            issue and passes the real question gate before it exists. */}

        <div className="pt-2">
          <label
            htmlFor="fee_type_described"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {lc.fee_description_label || "Anything else you'd like us to know? (optional)"}
          </label>
          <textarea
            id="fee_type_described"
            {...form.getInputProps("fee_type_described")}
            rows={2}
            placeholder={lc.fee_description_placeholder || "Describe what happened"}
            className={inputClass}
          />
        </div>

        {/* Marketing-email preference — pre-checked (opt-out posture), governs
            ONLY marketing email; unticking is honored via a suppression.
            Rendered ONLY when the server delivered the copy: an unlabeled
            checkbox would record consent_terms the visitor never saw
            (fail-closed — no copy means no preference collected). */}
        {marketing_consent_copy && (
          <div className="pt-2">
            <label
              htmlFor="marketing_consent"
              className="flex items-start gap-2 cursor-pointer min-h-[44px]"
            >
              <input
                id="marketing_consent"
                type="checkbox"
                checked={form.values.marketing_consent === true}
                onChange={(e) => form.setFieldValue("marketing_consent", e.target.checked)}
                className="mt-1"
              />
              {/* Server prop only — no hardcoded fallback. The server records
                Claimant::MARKETING_CONSENT_COPY as the consent terms, so the
                displayed copy must come from the same source or a future copy
                override would silently diverge from what gets recorded. */}
              <span className="text-sm text-gray-600">{marketing_consent_copy}</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              We'll always email you about your claim.
            </p>
          </div>
        )}

        <div className="pt-2">
          <Turnstile {...turnstile.widgetProps} />
          {/* Which failures the visitor needs to hear about is the hook's call
              (`failure.stuck`, see OUTCOMES in useTurnstile); only the copy is
              this page's — and it says they can still submit, because they can. */}
          {turnstile.failure?.stuck && (
            <p role="alert" className="text-xs text-rose-600 mt-2">
              The security check couldn't load in this browser. You can still tap Sign Up — if that
              doesn't work, refresh the page or open it in your usual browser.
            </p>
          )}
        </div>

        <p className="text-xs text-gray-600 leading-relaxed">{feeDisclosure}</p>

        <button type="submit" className={primaryBtn} disabled={submitting || !!preview}>
          {submitLabel}
        </button>
      </form>
    </div>
  )
}
